import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { AccessDeniedComponent } from './error-pages/access-denied.component';
import { AlertService } from './_services/alert.service';
import { AppComponent } from './app.component';
import { ApplicationService } from './_services/application.service';
import { AuthenticationService } from './_services/authentication.service';
import { Base64 } from './_pipes/base64.pipe';
import { DaysToOrDate } from './_pipes/days-to-or-date.pipe';
import { DummyComponent } from './print-permit-dummy-page/dummy.component';
import { ErrorInterceptor } from './error-pages/error-interceptor.service';
import { HoursFromOrDate } from './_pipes/hours-from-or-date.pipe';
import { LoggedInComponent } from './authentication/logged-in.component';
import { NotFoundComponent } from './error-pages/not-found/not-found.component';
import { ServerErrorComponent } from './error-pages//server-error/server-error.component';
import { PayGovMocksModule } from './pay-gov-mocks/pay-gov-mocks.module';
import { SharedModule } from './shared/shared.module';
import { SpecialUseInfoService } from './_services/special-use-info.service';
import { SortArray } from './_pipes/sort-array.pipe';
import { StyleGuideComponent } from './style-guide/style-guide.component';
import { TitleDirective } from './_directives/title.directive';
import { ForestsModule } from './forests/forests.module'
import { ShutdownComponent } from './shutdown/shutdown.component';
import { UtilService } from './_services/util.service';
import { WindowRef } from './_services/native-window.service';
import { PageHeaderComponent } from './shared/header/header.component';
import { PageFooterComponent } from './shared/footer/footer.component';
import { TableComponent } from './shared/table/table.component'
import { DropdownComponent } from './shared/dropdown/dropdown.component'
import { UserResolver } from './user-resolver.service';
import { GoogleAnalyticsService } from './_services/google-analytics.service';
import { MainLandingComponent } from './main-landing/main-landing.component';
import { ForestFinderComponent } from './forests/forest-finder/forest-finder.component'
import { OrderConfirmationComponent } from './forests/order-confirmation/order-confirmation.component';
import { BuyPermitsComponent } from './buy-permits/buy-permits.component';
import { StepperComponent } from './buy-permits/stepper/stepper.component';
import { BuyPermitsHeaderComponent } from './buy-permits/header/buy-permits-header.component';
import { PermitInformation } from './buy-permits/step-components/permit-information/permit-information.component';
import { PurchasePermit } from './buy-permits/step-components/purchase-permit/purchase-permit.component';
import { RulesAgreement } from './buy-permits/step-components/rules-agreement/rules-agreement.component';
import { AboutComponent } from './about/about.component';
import { RecursiveList } from './buy-permits/step-components/permit-information/recursive-list/recursive-list.component';
import { FSAppsService } from './_services/fsapps.service';
import { NrmService } from './_services/nrm.service';
import { FeedbackService } from './_services/feedback.service'
import { SignInComponent } from './authentication/sign-in.component';
import { PhoneFormatePipe } from './buy-permits/step-components/purchase-permit/phone-format.pipe';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { FeedbackComponent } from './feedback/feedback.component';
import { StatesFormatePipe } from './forests/forest-finder/states-format.pipe';
import { AdminPermitsComponent } from './admin/permits/permits.component'
import { AdminPermitStatBoxesComponent } from './admin/permits/permit-stat-boxes/permits-stat-boxes.component';
import { AdminLoginComponent } from './admin/login/login.component'
import { AdminUsersComponent } from './admin/users/users.component'
import { AdminManageUsersComponent } from './admin/manage-users/manage-users.component'
import { AdminHeaderComponent } from './admin/admin-header/admin-header.component';
import { ModalComponent } from './shared/modal/modal.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { GovBannerComponent } from './shared/header/gov-banner/gov-banner.component';
import { FaqComponent } from './faq/faq.component';
import { ScrollToTopComponent } from './shared/scroll-to-top/app-scroll-to-top.component';
import { PayGovErrorComponent } from './error-pages/paygov-error/paygov-error.component';
import { ButtonModalComponent } from "./shared/button-modal/button-modal.component";
import { AlertComponent } from './shared/alert/alert.component';
import { ManageAlertsComponent } from './admin/manage-alerts/manage-alerts.component';
import { SiteSettingsComponent } from './site-settings/site-settings.component';
import { ClickOutsideDirective } from './admin/permits/click-outside.directive';
import { AdminManagePermitsComponent } from './admin/manage-permits/manage-permits.component';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';

@NgModule({ 
    
    declarations: [
        AccessDeniedComponent,
        AppComponent,
        Base64,
        DaysToOrDate,
        DummyComponent,
        HoursFromOrDate,
        LoggedInComponent,
        NotFoundComponent,
        ServerErrorComponent,
        ShutdownComponent,
        PageFooterComponent,
        PageHeaderComponent,
        TableComponent,
        DropdownComponent,
        SortArray,
        StyleGuideComponent,
        TitleDirective,
        MainLandingComponent,
        ForestFinderComponent,
        OrderConfirmationComponent,
        BuyPermitsComponent,
        StepperComponent,
        BuyPermitsHeaderComponent,
        PermitInformation,
        PurchasePermit,
        RulesAgreement,
        AboutComponent,
        RecursiveList,
        SignInComponent,
        PhoneFormatePipe,
        FeedbackComponent,
        StatesFormatePipe,
        AdminLoginComponent,
        AdminUsersComponent,
        AdminPermitsComponent,
        AdminManageUsersComponent,
        AdminHeaderComponent,
        AdminPermitStatBoxesComponent,
        ModalComponent,
        SpinnerComponent,
        GovBannerComponent,
        FaqComponent,
        ScrollToTopComponent,
        PayGovErrorComponent,
        ButtonModalComponent,
        AlertComponent,
        ManageAlertsComponent,
        SiteSettingsComponent,
        ClickOutsideDirective,
        AdminManagePermitsComponent,
    ],
    bootstrap: [
        AppComponent,
    ],
    imports: [
        BrowserModule,
        CommonModule,
        RouterLink,
        PayGovMocksModule,
        SharedModule,
        ForestsModule,
        BreadcrumbComponent,
    ],
    providers: [
        AlertService,
        ApplicationService,
        AuthenticationService,
        GoogleAnalyticsService,
        SpecialUseInfoService,
        FSAppsService,
        NrmService,
        FeedbackService,
        UtilService,
        UserResolver,
        WindowRef,
        
        {   provide: LocationStrategy, 
            useClass: PathLocationStrategy 
        }, 
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true
        },

        provideAppInitializer(() => {
        const initializerFn = ((service: AuthenticationService) => () => {
                return service.getAuthenticatedUser(true).pipe(catchError(err => {
                    return of(null);
                }));
            })(inject(AuthenticationService));

        return initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi())
    ], 
})

export class AppModule {
  constructor(protected _googleAnalyticsService: GoogleAnalyticsService) {}
}
